import Grid from "@mui/material/Grid2";
import React, { forwardRef, useEffect, useState } from "react";
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Checkbox, 
  Dialog, 
  FormControlLabel, 
  Slide, 
  Table, 
  TableBody, 
  TableCell, 
  TableRow, 
  Typography 
} from "@mui/material";
import "./OrderConfirmModal.css";

const Transition = forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const MEAL_PLAN_FLAG_BY_MEAL_ID = { 1: "isBreakfast", 2: "isLunch", 3: "isDinner" };

export default function OrderConfirmModal({ 
  open, 
  orderData, 
  selectedSubRecipes, 
  confirmType, 
  handleClose, 
  isNoCharge,
  isGuest,
  residentMealPlanCharge,
  mealPlans,
  mealCountUsage,
  mealCostUsage,
  currentMealId
}) {
  const [guestPrice, setGuestPrice] = useState(orderData?.guestPrice || 0.00);
  const facilityMealPrice = orderData?.facilityMealPrice || 0.00;
  const addlCharges = orderData?.order?.additionalCharges || 0.00; //custom charge
  const otherCharges = orderData?.order?.orderCharges?.reduce((acc, curr) => acc += curr?.price, 0); //facility level charges
  const orderTotal = Math.max(guestPrice, facilityMealPrice) + addlCharges + otherCharges;
  const menuPriceString = `$${orderTotal.toFixed(2)}`;
  const [originalData, setOriginalData] = useState({});
  const [noChargeItems, setNoChargeItems] = useState([]);
  const [noPriceItems, setNoPriceItems] = useState([]); //items without price don't get "No Charge" checkbox
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noChargeCount, setNoChargeCount] = useState(false);
  const [noChargeCost, setNoChargeCost] = useState(false);

  useEffect(() => {
    if (orderData) {
      setOriginalData(() => structuredClone(orderData));
      const noChargeMenu = orderData?.menuItems?.filter((item) => !item?.guestPrice) || [];
      const noChargeAlt = orderData?.alternateItems?.filter((item) => !item?.guestPrice) || [];
      setNoChargeItems(() => [...noChargeMenu, ...noChargeAlt])
      const noPriceMenu = orderData?.menuChoices?.filter((item) => !item?.guestPrice) || [];
      const noPriceAlt = orderData?.alternateChoices?.filter((item) => !item?.guestPrice) || [];
      setNoPriceItems(() => [...noPriceMenu, ...noPriceAlt]);
      setGuestPrice(isNoCharge ? 0 : guestPrice);
      setNoChargeCount(!!orderData?.order?.noChargeCount);
      setNoChargeCost(!!orderData?.order?.noChargeCost);
    }
  }, [orderData]);

  // guest orders only carry mealPlans/usages here when the resident's meal plan charge flag applies (fetched upstream in Order)
  const mealCountPlan = mealPlans?.find((plan) => plan.mealPlanId === 2);
  const mealCostPlan = mealPlans?.find((plan) => plan.mealPlanId === 1);
  const showMealPlanSection = !isNoCharge && (mealCountPlan || mealCostPlan);

  const getMealPlanTypes = (plan) => {
    if (plan?.isBreakfast && plan?.isLunch && plan?.isDinner) return "All Meals";

    const types = [];
    if (plan?.isBreakfast) types.push("Breakfast");
    if (plan?.isLunch) types.push("Lunch");
    if (plan?.isDinner) types.push("Dinner");
    return types.join(", ");
  };

  const isPlanApplicable = (plan) => {
    const flag = MEAL_PLAN_FLAG_BY_MEAL_ID[currentMealId];
    return !!(plan && flag && plan[flag]);
  };

  const isGuestMealPlanOverride = isGuest && residentMealPlanCharge;

  const countApplicable = isGuestMealPlanOverride ? !!mealCountPlan : isPlanApplicable(mealCountPlan);
  // a $0 meal has nothing to charge against the cost plan, so there's no override to offer
  const costApplicable = isGuestMealPlanOverride
    ? !!mealCostPlan && orderTotal > 0
    : isPlanApplicable(mealCostPlan) && orderTotal > 0;

  // guest orders already chose to charge the resident directly; only allow opting out when both plan types are simultaneously being charged against
  const guestBothChargesApplicable = countApplicable && costApplicable;
  const showCountCheckbox = isGuestMealPlanOverride ? guestBothChargesApplicable : countApplicable;
  const showCostCheckbox = isGuestMealPlanOverride ? guestBothChargesApplicable : costApplicable;

  const countDelta = countApplicable && !noChargeCount ? 1 : 0;
  const costDelta = costApplicable && !noChargeCost ? orderTotal : 0;

  // guest checkboxes are mutually exclusive — waiving one re-charges the other; resident flow keeps independent toggles
  const handleNoChargeCountChange = () => {
    setNoChargeCount((prev) => {
      const next = !prev;
      if (isGuestMealPlanOverride && next) setNoChargeCost(false);
      return next;
    });
  };

  const handleNoChargeCostChange = () => {
    setNoChargeCost((prev) => {
      const next = !prev;
      if (isGuestMealPlanOverride && next) setNoChargeCount(false);
      return next;
    });
  };

  const renderMealPlanRow = ({
    title,
    subtitle,
    applicable,
    showCheckbox,
    offsetLabel,
    primaryValue,
    primaryLabel,
    secondaryValue,
    secondaryLabel,
    checked,
    onCheckChange,
  }) => (
    <Box sx={{ display: "flex", flexWrap: "nowrap", alignItems: "center", width: "100%" }}>
      <Box sx={{ width: "8rem" }}>
        <Typography variant="body2" fontWeight="bold">{title}</Typography>
        <Typography variant="caption" color="text.secondary">{isGuest ? "" : subtitle}</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: "center" }}>
        <Typography variant="body2" fontWeight="bold" color={applicable && !checked ? "error.main" : "text.primary"}>
          {offsetLabel}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: "center" }}>
        <Typography variant="body2" fontWeight="bold">{primaryValue}</Typography>
        <Typography variant="caption" color="text.secondary">{primaryLabel}</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: "center" }}>
        <Typography variant="body2" fontWeight="bold">{secondaryValue}</Typography>
        <Typography variant="caption" color="text.secondary">{secondaryLabel}</Typography>
      </Box>
      <Box sx={{ width: "5rem", textAlign: "center" }}>
        {showCheckbox && (
          <FormControlLabel
            sx={{ margin: 0, "& .MuiTypography-root": { fontSize: ".7rem" } }}
            labelPlacement="top"
            control={<Checkbox size="small" checked={checked} onChange={onCheckChange} />}
            label="No Charge"
          />
        )}
      </Box>
    </Box>
  );

  const renderMealPlanBalance = () => {
    const rows = [];

    if (mealCountPlan) {
      const projectedUsage = (mealCountUsage ?? 0) + countDelta;
      const countLimit = mealCountPlan.mealCountLimit;
      rows.push(
        renderMealPlanRow({
          title: "Meal Count",
          subtitle: getMealPlanTypes(mealCountPlan),
          applicable: countApplicable,
          showCheckbox: showCountCheckbox,
          offsetLabel: countApplicable ? (noChargeCount ? "No Charge" : "1 Meal") : "No Charge",
          primaryValue: `${projectedUsage} meals`,
          primaryLabel: "Used",
          secondaryValue: countLimit != null ? countLimit - projectedUsage : "No Limit",
          secondaryLabel: countLimit != null ? "Remaining" : "",
          checked: noChargeCount,
          onCheckChange: handleNoChargeCountChange,
        })
      );
    }

    if (mealCountPlan && mealCostPlan) {
      rows.push(<Box sx={{borderTop: "1px solid lightgray", margin: "-.5rem 0 0"}}></Box>)
    }

    if (mealCostPlan) {
      const costLimit = mealCostPlan.mealCostLimit;
      const projectedUsage = (mealCostUsage ?? 0) + (costLimit != null ? costDelta : -costDelta);
      // rollover plans carry a running balance across periods; non-rollover plans reset each period, so remaining starts as limit minus usage
      const baseRemaining = mealCostPlan.isAllowedRollOver
        ? (mealCostPlan.remainingCostBalance ?? 0)
        : costLimit - (mealCostUsage ?? 0);
      const remaining = baseRemaining - costDelta;
      rows.push(
        renderMealPlanRow({
          title: "Meal Cost",
          subtitle: getMealPlanTypes(mealCostPlan),
          applicable: costApplicable,
          showCheckbox: showCostCheckbox,
          offsetLabel: costApplicable ? (noChargeCost ? "No Charge" : `$${orderTotal.toFixed(2)}`) : "No Charge",
          primaryValue: `$${projectedUsage.toFixed(2)}`,
          primaryLabel: costLimit != null ? "Used" : "Balance",
          secondaryValue: costLimit != null ? `$${remaining.toFixed(2)}` : "No Limit",
          secondaryLabel: costLimit != null ? "Remaining" : "",
          checked: noChargeCost,
          onCheckChange: handleNoChargeCostChange,
        })
      );
    }

    return rows;
  };

  const handleNoChargeClick = (item, type) => {
    const idx = noChargeItems?.findIndex((removed) => removed.itemId === item.itemId);

    if (idx >= 0) {
      
      item.guestPrice = orderData[type].find((recipe) => recipe.recipeId === item.itemId)?.guestPrice || 0.00;
      setGuestPrice((prev) => prev + (item.guestPrice ?? 0.00));
      
      setNoChargeItems((prev) => {
        const noChargeCopy = [...prev];
        noChargeCopy.splice(idx, 1);
        return noChargeCopy;
      });
    } else {
      setGuestPrice((prev) => prev - (item.guestPrice ?? 0.00));
      setNoChargeItems((prev) => [...prev, item]);
      item.guestPrice = 0.00;
    }
  }

  const renderMenuItems = () => {
    const menuItems = [];

    orderData.menuItems?.forEach((item, idx) => {
      const menuChoice = orderData.menuChoices?.find((choice) => choice.recipeId === item.itemId);
      const subRecipes = selectedSubRecipes?.filter((sub) => sub.parentRecipeId === item.itemId) || [];

      menuItems.push(
        <TableRow key={`${item.itemId}-${idx}`} className="tableRow">  
          <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
            <Typography variant="body1">{menuChoice?.recipeName}</Typography>
          </TableCell>

          <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
            <Typography variant="body1">{item.quantity}</Typography>
          </TableCell>

          <TableCell 
            sx={{
              alignSelf: "center", 
              textAlign: "center", 
              borderBottom: "none", 
              paddingX: ".25rem !important"
            }}
            onClick={(ev) => ev.preventDefault()}
          >
            {!noPriceItems.some((noPrice) => noPrice.recipeId === item.itemId) && !isNoCharge &&
              <FormControlLabel
                sx={{
                  marginX: "0px !important",
                  "& .MuiTypography-root": { fontSize: ".875rem" },
                  "& .MuiButtonBase-root.MuiCheckbox-root": { padding: ".25rem" }
                }}
                control={
                  <Checkbox size="small"
                    checked={noChargeItems.some((removed) => removed.itemId === item.itemId)}
                    onClick={() => handleNoChargeClick(item, 'menuChoices')}
                  />
                }
                label="No Charge"
                labelPlacement="top"
              />
            }
          </TableCell>
        </TableRow>
      );

      if (item?.specialNote) {
        subRecipes.splice(0, 0, {recipeId: 0, recipeName: `Special Note: ${item.specialNote}`});
      }

      if (item?.allergyNote) {
        subRecipes.splice(0, 0, {recipeId: -1, recipeName: item.allergyNote});
      }

      if (item?.removedItems?.length) {
        const subMapping = item.removedItems.map((removed, idx) => {
          return {
            recipeId: -2 - idx,
            recipeName: `Remove - ${removed.text} (ALLERGY)`,
            isAllergyNote: true
          }
        });
        
        subRecipes.splice(0, 0, ...subMapping);
      }

      subRecipes.forEach((sub, idx) => {
        menuItems.push(
          <TableRow key={`${sub.recipeId}-${idx}`} className="tableRow">
            <TableCell sx={{alignSelf: "center", paddingLeft: "3rem !important", borderBottom: "none"}}>
              <Typography variant="body1">{sub?.recipeName}</Typography>
            </TableCell>
            <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
              <Typography variant="body1">{sub?.quantity}</Typography>
            </TableCell>
          </TableRow>
        );
      })
    });

    return menuItems;
  }

  const renderAltItems = () => {
    const altItems = [];

    orderData.alternateItems?.forEach((item, idx) => {
      const menuChoice = orderData.alternateChoices?.find((choice) => choice.recipeId === item.itemId);
      const subRecipes = selectedSubRecipes?.filter((sub) => sub.parentRecipeId === item.itemId) || [];
      altItems.push(
        <TableRow key={`${item.itemId}-${idx}`} className="tableRow">  
          <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
            <Typography variant="body1">{menuChoice?.recipeName}</Typography>
          </TableCell>

          <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
            <Typography variant="body1">{item?.quantity}</Typography>
          </TableCell>

          <TableCell 
            sx={{
              alignSelf: "center", 
              textAlign: "center", 
              borderBottom: "none", 
              paddingX: ".25rem !important"
            }}
            onClick={(ev) => ev.preventDefault()}
          >            
            {!noPriceItems.some((noPrice) => noPrice.recipeId === item.itemId) && !isNoCharge &&
              <FormControlLabel
                sx={{
                  marginX: "0px !important",
                  "& .MuiTypography-root": { fontSize: ".875rem" },
                  "& .MuiButtonBase-root.MuiCheckbox-root": { padding: ".25rem" }
                }}
                control={
                  <Checkbox size="small"
                    checked={noChargeItems.some((removed) => removed.itemId === item.itemId)}
                    onClick={() => handleNoChargeClick(item, 'alternateChoices')}
                  />
                }
                label="No Charge"
                labelPlacement="top"
              />
            }
          </TableCell>
        </TableRow>
      );

      if (item?.specialNote) {
        subRecipes.splice(0, 0, {recipeId: 0, recipeName: `Special Note: ${item.specialNote}`});
      }

      if (item?.allergyNote) {
        subRecipes.splice(0, 0, {recipeId: -1, recipeName: item.allergyNote});
      }

      if (item?.removedItems?.length) {
        const subMapping = item.removedItems.map((removed, idx) => {
          return {
            recipeId: -2 - idx,
            recipeName: `Remove - ${removed.text} (ALLERGY)`,
            isAllergyNote: true
          }
        });
        
        subRecipes.splice(0, 0, ...subMapping);
      }

      subRecipes.forEach((sub, idx) => {
        altItems.push(
          <TableRow key={`${sub.recipeId}-${idx}`} className="tableRow">
            <TableCell sx={{alignSelf: "center", paddingLeft: "3rem !important", borderBottom: "none"}}>
              <Typography variant="body1">{sub?.recipeName}</Typography>
            </TableCell>
            <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
              <Typography variant="body1">{sub?.quantity}</Typography>
            </TableCell>
          </TableRow>
        );
      })
    });

    return altItems;
  }

  const renderPriceBreakdown = () => {
    const priceList = [];

    priceList.push(
      <TableRow key="facilityMealPrice" className="priceRow">
        <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
          <Typography variant="body1" gutterBottom>{`${orderData?.mealName?.split('-')?.[0]?.trim()}`}</Typography>
        </TableCell>
        <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
          <Typography variant="body1" gutterBottom>{`$${isNoCharge ? "0.00" : (Math.max(guestPrice, facilityMealPrice)).toFixed(2)}`}</Typography>
        </TableCell>
      </TableRow>
    );
    

    orderData?.order?.orderCharges?.forEach((charge, idx) => {
      priceList.push(
        <TableRow key={`facCharge-${idx}`} className="priceRow">
          <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
            <Typography variant="body1" gutterBottom>{`${charge?.chargeTypeId === 6 ? `${charge.chargeTypeText}`: `${charge.chargeTypeName}`}`}</Typography>
          </TableCell>
          <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
            <Typography variant="body1" gutterBottom>{`$${isNoCharge ? "0.00" : charge?.price?.toFixed(2)}`}</Typography>
          </TableCell>
        </TableRow>
      );
    });

    if (addlCharges || orderData?.order?.additionalChargeNote?.length) {
      priceList.push(
        <TableRow key={`addlCharge`} className="priceRow">
          <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
            <Typography variant="body1" gutterBottom>{`${orderData?.order?.additionalChargeNote}`}</Typography>
          </TableCell>
          <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
            <Typography variant="body1" gutterBottom>{`$${isNoCharge ? "0.00" : addlCharges.toFixed(2)}`}</Typography>
          </TableCell>
        </TableRow>

      );
    }

    priceList.push(        
      <TableRow key="totalPrice" className="priceRow">
      <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
        <Typography variant="body1" fontWeight="bold" gutterBottom>{`${isNoCharge ? "Total (No Charge)" : "Total"}`}</Typography>
      </TableCell>
      <TableCell sx={{alignSelf: "center", borderBottom: "none"}}>
        <Typography variant="body1" fontWeight="bold" gutterBottom>{`${isNoCharge ? "$0.00" : menuPriceString}`}</Typography>
      </TableCell>
    </TableRow>
    );

    return priceList;
  }

  const handleSubmit = () => {
    if (isSubmitting) return;

    orderData.guestPrice = isNoCharge ? 0 : Math.max(guestPrice, facilityMealPrice);
    orderData.order.noChargeCount = noChargeCount;
    orderData.order.noChargeCost = noChargeCost;

    setIsSubmitting(true);
    handleClose(true);
  }

  const beforeClose = () => {
    orderData.alternateItems = [...originalData.alternateItems];
    orderData.menuItems = [...originalData.menuItems];
    handleClose(false);
  }

  return (
    <Dialog
      open={open}
      onClose={() => beforeClose()}
      TransitionComponent={Transition}
    >
      <Box 
        sx={{
          maxWidth: "600px", 
          height: "auto", 
          maxHeight: "calc(100vh - 70px) !important"
        }} 
        className="paperContent customScrollbar" 
        justifyContent="center" 
        alignItems="flex-start" 
        paddingY={2}
        paddingX={2.5}
      >
        <Typography 
          variant="h6" 
          gutterBottom 
          textAlign="left"
          fontWeight="bold"
          color="var(--tealColor)"
          sx={{ borderBottom: "3px solid var(--tealColor)", marginBottom: "1rem", paddingBottom: ".25rem"}}
        >
          Review Order
        </Typography>
        {!!orderData?.menuItems?.length && <Card sx={{marginBottom: "1rem"}}>
          <CardContent>
            <Typography variant="body1" gutterBottom textAlign="left" fontWeight="bold">Daily Special</Typography>
            <Table>
              <TableBody>
                {renderMenuItems()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>}

        {!!orderData?.alternateItems?.length && <Card sx={{marginBottom: "1rem"}}>
          <CardContent>
            <Typography variant="body1" gutterBottom textAlign="left" fontWeight="bold">Other Menu Options</Typography>
            <Table>
              <TableBody>
                {renderAltItems()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>}

        {orderData?.order?.note && (
          <Card sx={{marginBottom: "1rem"}}>
            <CardContent>
              <Typography width="100%" variant="body1" gutterBottom textAlign="left" fontWeight="bold">Note</Typography>
              <Grid sx={{display: "flex"}} justifyContent="flex-start" paddingX={2}>
                <Typography variant="body1">{orderData.order.note}</Typography>
              </Grid>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Grid container justifyContent="space-between">
              <Typography width="100%"  variant="body1" gutterBottom textAlign="left" fontWeight="bold">Meal Price</Typography>
              <Table>
                <TableBody>
                  {renderPriceBreakdown()}
                </TableBody>
              </Table>
            </Grid>
          </CardContent>
        </Card>

        {showMealPlanSection && (
          <Card sx={{marginTop: "1rem"}}>
            <CardContent padding="1rem 1rem .5rem">
              <Typography 
                variant="body1" 
                gutterBottom 
                textAlign="left" 
                fontWeight="bold" 
                color="var(--tealColor)"
              >
                {isGuest ? "Resident's" : ""} Meal Plan Balance
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {renderMealPlanBalance()}
              </Box>
            </CardContent>
          </Card>
        )}

        <Grid container justifyContent="center" paddingY={3} paddingX={1}>
          <Button
            sx={{marginRight: ".5rem", width: "calc(50% - .5rem)"}}
            variant="contained"
            disabled={isSubmitting}
            onClick={() => handleSubmit()}
          >
            {confirmType}
          </Button>
          <Button
            sx={{width: "calc(50% - .5rem)"}}
            variant="contained"
            onClick={() => beforeClose()}
          >
            Cancel
          </Button>
        </Grid>
      </Box>
    </Dialog>
  )
}